import React, {useCallback} from 'react';
import {ListItem} from '@mui/material';
import {MatrixEvent} from 'matrix-js-sdk';
import {
  formatTime,
  isSystemMessage
} from '@/components/matrix-chat/utils/helper-functions';
import SystemMessage from '@/components/matrix-chat/message/text-message/system-message';
import UserMatrixEvent from '@/components/matrix-chat/message/text-message/user-message';
import {BoxPosition, EventPosition, MultimediaEventTypes} from '@/types/matrix';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import ListDivider from '@/components/matrix-chat/ui/list-divider';
import MultiImageBox from '@/components/matrix-chat/message/multimedia-message/image-message';
import MultimediaLoadingBox from '@/components/matrix-chat/ui/multimedia-loading-box';
import MultiVideoBox from '@/components/matrix-chat/message/multimedia-message/video-message';

interface EventItemProps {
  event: MatrixEvent;
  memberId: string;
  eventIdx: number;
  timeGroup: MatrixEvent[];
  numUnreadEventInList: number;
  lastReadEventInList: string | null;
  isImageGroup: boolean;
  isVideoGroup: boolean;
}

const EventItem: React.FC<EventItemProps> = ({
  event,
  memberId,
  eventIdx,
  timeGroup,
  numUnreadEventInList,
  lastReadEventInList,
  isImageGroup,
  isVideoGroup
}) => {
  const effectiveEvent = event.getEffectiveEvent();

  const key = effectiveEvent?.event_id;

  const isEventSystemMessage = event && isSystemMessage(event);

  const {handleMouseEnterEvent, handleMouseLeaveEvent, multimediaEventsToSend} =
    useMatrixRoomContext();

  const isLastEventBeforeDivider =
    effectiveEvent && effectiveEvent.event_id === lastReadEventInList;

  const isOnlyEventBeforeDivider = isLastEventBeforeDivider && eventIdx === 0;

  const isFirstEventAfterDivider =
    lastReadEventInList &&
    eventIdx ===
      timeGroup.findIndex(
        (e) => e.getEffectiveEvent().event_id === lastReadEventInList
      ) +
        1;

  let position: EventPosition = 'single';

  if (isOnlyEventBeforeDivider) {
    position = 'single';
  } else if (isLastEventBeforeDivider) {
    position = 'last';
  } else if (isFirstEventAfterDivider) {
    const moreThanOneEventAfterDivider = eventIdx + 1 < timeGroup.length;
    position = moreThanOneEventAfterDivider ? 'first' : 'single';
  } else if (timeGroup.length > 1) {
    if (eventIdx === 0) {
      position = 'first';
    } else if (eventIdx === timeGroup.length - 1) {
      position = 'last';
    } else {
      position = 'middle';
    }
  }

  // const position = useMemo(() => {
  //   if (isOnlyEventBeforeDivider) {
  //     return 'single';
  //   } else if (isLastEventBeforeDivider) {
  //     return 'last';
  //   } else if (isFirstEventAfterDivider) {
  //     return eventIdx + 1 < timeGroup.length ? 'first' : 'single';
  //   } else if (timeGroup.length > 1) {
  //     if (eventIdx === 0) {
  //       return 'first';
  //     } else if (eventIdx === timeGroup.length - 1) {
  //       return 'last';
  //     }
  //     return 'middle';
  //   }
  //   return 'single';
  // }, [
  //   isOnlyEventBeforeDivider,
  //   isLastEventBeforeDivider,
  //   isFirstEventAfterDivider,
  //   eventIdx,
  //   timeGroup
  // ]);

  // TODO: Try to use useMemo?

  const getMultimediaLoadingBoxPosition = useCallback(() => {
    let boxPosition: BoxPosition = 'single';
    const currentTimestamp = new Date().toISOString();

    const formattedCurrentTime = formatTime(currentTimestamp);

    const eventTs = event.getTs();
    const eventTime = new Date(eventTs).toISOString();
    const formattedEventTIme = formatTime(eventTime);

    const eventMessageType = event.getEffectiveEvent().content.msgtype;

    const isMultimediaType =
      eventMessageType && MultimediaEventTypes.includes(eventMessageType);

    if (formattedCurrentTime === formattedEventTIme && isMultimediaType) {
      boxPosition = 'last';
    }

    return boxPosition;
  }, [event]);

  const isOwnMatrixEvent =
    (effectiveEvent &&
      effectiveEvent.sender === memberId &&
      !isEventSystemMessage) ||
    multimediaEventsToSend > 0;

  const prevMatrixEventSentBySystem =
    eventIdx > 0 && isSystemMessage(timeGroup[eventIdx - 1]);

  const additionalTopMargin =
    prevMatrixEventSentBySystem && !isEventSystemMessage && !isOwnMatrixEvent;

  const handleMouseEnter = useCallback(
    (eventId: string) => {
      handleMouseEnterEvent(eventId);
    },
    [handleMouseEnterEvent]
  );

  const handleMouseLeave = useCallback(() => {
    handleMouseLeaveEvent();
  }, [handleMouseLeaveEvent]);

  return (
    <div
      key={key}
      onMouseEnter={() => {
        if (effectiveEvent) {
          handleMouseEnter(effectiveEvent.event_id);
        }
      }}
      onMouseLeave={handleMouseLeave}
      style={{flex: 1}}
    >
      <ListItem
        key={key}
        sx={{
          justifyContent: isOwnMatrixEvent ? 'flex-end' : 'flex-start',
          flexDirection: 'column',
          alignItems: isOwnMatrixEvent ? 'flex-end' : 'flex-start',
          m: 0,
          p: 0,
          mt: additionalTopMargin
            ? 4
            : eventIdx === 0 || isEventSystemMessage
            ? 3
            : 0,
          mb: isEventSystemMessage ? 3 : 0
        }}
      >
        {isEventSystemMessage ? (
          <SystemMessage content={event} />
        ) : isImageGroup ? (
          <MultiImageBox imageEvent={event} eventPosition={position} />
        ) : isVideoGroup ? (
          <MultiVideoBox videoEvent={event} eventPosition={position} />
        ) : (
          <UserMatrixEvent matrixEvent={event} eventPosition={position} />
        )}
        {multimediaEventsToSend > 0 && (
          <MultimediaLoadingBox
            multimediaCount={multimediaEventsToSend}
            boxPosition={getMultimediaLoadingBoxPosition()}
          />
        )}
      </ListItem>
      {isLastEventBeforeDivider && numUnreadEventInList > 0 && (
        <ListDivider unreadEventsCount={numUnreadEventInList} />
      )}
    </div>
  );
};

export default EventItem;
